import { useMutation } from '@apollo/react-hooks';
import * as PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import S from './group-sub-header.module.scss';
import { DropDownWithSelected } from '../../../../../common/drop-down-with-selected';
import { useModal } from '../../../../../global-hooks';
import { UI_MODALS } from '../../../../../../helpers/enums';
import { logError } from '../../../../../../helpers/errors/bug-report';
import { GROUP_DANCERS_REMOVE_GQL } from '../../../../../../graphql/mutations/group-dancers-remove';

const GroupPageSubHeader = ({ groupId, dancers, onDancersRemove, selectedDancers, selectDancers, deselectDancers }) => {
  const { t } = useTranslation();

  const { initModal } = useModal();

  const handleAddGroup = useCallback(() => initModal(UI_MODALS.ADD_TO_GROUP, { dancerIds: selectedDancers }), [initModal, selectedDancers]);

  const handleDeselectAll = useCallback(() => deselectDancers(), [deselectDancers]);

  const handleSelectAll = useCallback(() => selectDancers(), [selectDancers]);

  const [removeDancers] = useMutation(GROUP_DANCERS_REMOVE_GQL, {
    variables: {
      groupId,
      toRemove: selectedDancers,
    },
    update: (
      cache, _
    ) => {
      onDancersRemove &&  onDancersRemove(cache, selectedDancers);
    },
    onCompleted: async() => {
      deselectDancers(selectedDancers);
      toast.success(t('groupsPage.actions.remove_from_group.success'));
    },
    onError: (err) => {
      logError(err, 'GROUP_DANCERS_REMOVE_GQL', GroupPageSubHeader.displayName);
      toast.error(t('groupsPage.actions.remove_from_group.failure'));
    },
  });

  return (
    <div className={S.groupSubHeaderWrapper}>
      <DropDownWithSelected
        selectedCount={selectedDancers.length}
        totalCount={dancers.length}
        onDeselect={handleDeselectAll}
        onSelectAll={handleSelectAll}
      />

      <div className={S.groupSubHeaderActionArea}>
        {selectedDancers.length > 0 && (
          <>
            <button className={S.invertedBtnGreen} type='button' onClick={handleAddGroup}>
              {t('groupsPage.actions.add')}
            </button>
            <button className={S.invertedBtnRed} type='button' onClick={removeDancers}>
              {t('groupsPage.actions.remove')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

GroupPageSubHeader.displayName = 'GroupPageSubHeader';

GroupPageSubHeader.propTypes = {
  groupId: PropTypes.string.isRequired,
  dancers: PropTypes.array.isRequired,
  onDancersRemove: PropTypes.func,
  selectedDancers: PropTypes.arrayOf(PropTypes.string),
  selectDancers: PropTypes.func.isRequired,
  deselectDancers: PropTypes.func.isRequired,
};

GroupPageSubHeader.defaultProps = {
  selectedDancers: [],
  onDancersRemove: undefined,
};

export { GroupPageSubHeader };
